import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { getActiveSchemeClass } from '../utils/themeUtils';
import useBackdropClose from '../utils/useBackdropClose';

const IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
const FRAME_TYPES = ['pdf', 'txt'];

// Browsers can show images, PDFs and plain text in place; anything else
// (Word, Excel, ...) would just download, so those get a download prompt.
const previewKind = (fileName) => {
  const extension = `${fileName}`.split('.').pop().toLowerCase();
  if (IMAGE_TYPES.includes(extension)) return 'image';
  if (FRAME_TYPES.includes(extension)) return 'frame';
  return null;
};

const FilePreviewModal = ({ file, close }) => {
  const [loading, setLoading] = useState(true);
  const kind = previewKind(file.file_name);
  const backdrop = useBackdropClose(close);

  // Captured so Escape closes the preview without also closing the Files card behind it.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [close]);

  const download = () => window.open(file.url, '_blank', 'noopener,noreferrer');

  return createPortal(
    <div className={`file-preview-backdrop ${getActiveSchemeClass()}`.trim()} {...backdrop} role="presentation">
      <div className="file-preview-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Preview of ${file.file_name}`}>
        <div className="file-preview-header">
          <div className="file-preview-heading">
            <span className="file-preview-name">{file.file_name}</span>
            <span className="file-preview-meta">Uploaded {file.uploaded_formatted} by {file.uploaded_by}</span>
          </div>
          <button type="button" className="file-preview-action" onClick={download} aria-label="Download file">
            <DownloadIcon />
          </button>
          <button type="button" className="file-preview-action" onClick={close} aria-label="Close preview">
            <CloseIcon />
          </button>
        </div>

        <div className="file-preview-body">
          {kind && loading && <div className="file-preview-status">Loading preview…</div>}

          {kind === 'image' && (
            <img src={file.url} alt={file.file_name} className="file-preview-image" onLoad={() => setLoading(false)} onError={() => setLoading(false)} />
          )}

          {kind === 'frame' && (
            <iframe src={file.url} title={file.file_name} className="file-preview-frame" onLoad={() => setLoading(false)} />
          )}

          {!kind && (
            <div className="file-preview-unavailable">
              <p>This file type can't be previewed here.</p>
              <button type="button" className="file-preview-download" onClick={download}>
                <DownloadIcon /> Download file
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilePreviewModal;
